import {
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { colors } from 'design-system';
import React from 'react';
import { Check } from 'react-feather';
import CatalogItem from '../../../models/helperTypes/CatalogItem';
import BaseInputWrapper from '../BaseInputWrapper';
import InputProps from '../InputProps';
import useDefaultInputHook from '../hooks/useDefaultInputHook';

const useLocalStyles = makeStyles({
  defaultSelectOption: {
    height: 26,
    color: colors.abbey,
  },
  currentSelectOption: {
    height: 26,
    color: colors.shamrock,
    backgroundColor: 'transparent !important',
  },
  errorText: {
    color: colors.bright_pink,
  },
});

interface Props extends InputProps<string, CatalogItem> {
  options: CatalogItem[];
}

const DropDown = ({
  inputName,
  inputLabel,
  options,
  onChange,
  inputError = false,
  inputErrorMessage = '',
  isDisabled = false,
  defaultValue = '',
}: Props): JSX.Element => {
  const localClasses = useLocalStyles();
  const inputId = `drop-down-${inputName}`;
  const { currentClass, decoration, onInputChanged, onBlur } =
    useDefaultInputHook(inputError, defaultValue);

  const handleChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
    const valueOfDropDown = event.target.value as string;
    const result = options
      .filter((item: CatalogItem): boolean => item.Value === valueOfDropDown)
      .pop();
    if (typeof result !== 'undefined') {
      onChange(result);
      onInputChanged(result.Value);
    }
  };

  // Documentation is not clear on what type of value this method should return
  // to work with IconComponent (React.Element does not work), so for now the
  // return type will be ignored
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  function iconComponent() {
    return defaultValue === '' ? <ArrowDropDownIcon /> : <></>;
  }

  function renderValue(value: unknown): React.ReactNode {
    let renderedOption = <div>&nbsp;</div>;
    options.forEach((option: CatalogItem): void => {
      if (option.Value === value) {
        renderedOption = <div>{option.Text}</div>;
      }
    });
    return renderedOption;
  }

  return (
    <BaseInputWrapper
      inputId={inputId}
      label={inputLabel}
      error={inputError}
      errorMessage={inputErrorMessage}
    >
      <Select
        id={inputId}
        name={inputName}
        className={currentClass}
        value={defaultValue}
        onChange={(event): void => {
          handleChange(event);
          onBlur();
        }}
        error={inputError}
        disabled={isDisabled}
        onBlur={onBlur}
        IconComponent={iconComponent}
        renderValue={renderValue}
        endAdornment={
          <InputAdornment position="end">{decoration}</InputAdornment>
        }
      >
        {options.map((option: CatalogItem) => (
          <MenuItem
            className={
              option.Value === defaultValue
                ? localClasses.currentSelectOption
                : localClasses.defaultSelectOption
            }
            key={option.Value}
            value={option.Value}
          >
            <ListItemText
              primary={
                <Typography variant="caption" color="secondary">
                  {option.Text}
                </Typography>
              }
            />
            {option.Value === defaultValue && (
              <ListItemIcon style={{ display: 'block', textAlign: 'right' }}>
                <Check size={15} color={colors.shamrock} />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Select>
    </BaseInputWrapper>
  );
};

export default DropDown;
